import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import dayjs from 'core/utils/dayjs';
import { selectBrowserIsMobile } from 'core/selectors/user';
import { selectBonusTiersCount } from 'core/modules/BonusTiers/selectors';
import { objectFitPolyFillImage } from 'core/utils/helpers/objectFitPolyFill';

import BonusTiers from 'core/modules/BonusTiers/BonusTiers';

import Countdown from 'core/modules/Countdown/Countdown';

import './StylizedBannerItem.scss';

const DEBOUNCE_RESIZE_DELAY = 200;

function StylizedBannerItem({ dataItem, hasBonus }) {
  const [hasCountdown, setHasCountdown] = useState(false);
  const [countdownEndDate, setCountdownEndDate] = useState(undefined);
  const [imageHeight, setImageHeight] = useState(0);

  const isMobile = useSelector(selectBrowserIsMobile);
  const bonusTiersCount = useSelector(selectBonusTiersCount);
  const { assets, offerTitle } = dataItem || {};
  const {
    _1440x450, preHeadline, countdownClockPrefixText, rolloverCopy, shortSummaryText, countdownClockStopDate,
  } = assets || {};

  const imageRef = useRef();
  const bannerInfoRef = useRef();
  const bonusTiersRef = useRef();

  const setStylizedBannerImageHeight = () => {
    if (!imageRef.current || !bannerInfoRef.current) {
      return false;
    }

    const bonusTiersHeight = bonusTiersRef.current ? bonusTiersRef.current.clientHeight : 0;
    // desktop/tablet: 40px offset - from top 100px and from bottom 60px
    // mobile: 20px offset - from top 60px and from bottom 40px
    // Remove height of bonus tiers section
    const bannerInfoHeightPlusOffsetMinusBonusTiers =
      bannerInfoRef.current.clientHeight +
      (isMobile ? 20 : 40) -
      bonusTiersHeight;

    if (imageHeight !== bannerInfoHeightPlusOffsetMinusBonusTiers) {
      setImageHeight(bannerInfoHeightPlusOffsetMinusBonusTiers);
    }
    return true;
  };

  const setStylizedBannerImageHeightOnResize = debounce(setStylizedBannerImageHeight, DEBOUNCE_RESIZE_DELAY);

  const removeCountDown = () => {
    setHasCountdown(false);
  };

  useEffect(() => {
    const hasCountdown = countdownClockStopDate
      && dayjs(countdownClockStopDate).isValid()
      && dayjs(countdownClockStopDate).year() < 2099;
    const countdownEndDate = hasCountdown && countdownClockStopDate.length > 10
      ? countdownClockStopDate
      // When merch portal will allow date and time. Set the end of a day
      : dayjs(countdownClockStopDate).add(1, 'day').subtract(1, 'second').format();
    setHasCountdown(hasCountdown);
    setCountdownEndDate(countdownEndDate);

    window.addEventListener('resize', setStylizedBannerImageHeightOnResize);

    return () => window.removeEventListener('resize', setStylizedBannerImageHeightOnResize);
  }, []);

  if (imageRef.current && bannerInfoRef.current) {
    setTimeout(() => {
      setStylizedBannerImageHeight();
      objectFitPolyFillImage(imageRef.current);
    });
  }

  const bannerAdditionalClassName =
    (
      hasBonus &&
      bonusTiersCount > 1 &&
      (bonusTiersCount === 2 ? 'mn_withTiers' : 'mn_withTiers mn_threeTiers')
    ) || '';

  return (
    <div className={`mn_stylizedBanner ${bannerAdditionalClassName}`} data-test="stylized-banner-item">
      <div className="mn_imageWrap">
        <img
          data-test="stylized-banner-image"
          ref={imageRef}
          style={{ height: imageHeight }}
          data-object-fit="cover"
          src={_1440x450}
          alt=""
        />
      </div>
      <div
        ref={bannerInfoRef}
        className={`mn_bannerInfo ${hasCountdown ? '' : 'mn_bannerWithoutCountdownClock'}`}
      >
        <div className="mn_bannerInfoWrap">
          {hasCountdown && (
            <div className="mn_countdownClockInfoWrap">
              <div className="mn_countdownClockInfoWrapContainer">
                {
                  countdownClockPrefixText &&
                  <span className="mn_countdownPrefix" data-test="countdownClockPrefixText">
                    {countdownClockPrefixText}
                  </span>
                }
                <div className="mn_countdownClock">
                  <Countdown endDate={countdownEndDate} onComplete={removeCountDown} />
                </div>
              </div>
            </div>
          )}
          <div className="mn_headInfoWrap">
            {
              preHeadline &&
              <p
                data-test="preHeadline"
                className="mn_headline"
                dangerouslySetInnerHTML={{ __html: preHeadline }}
              />
            }
            { rolloverCopy ?
              <p
                className="mn_longSummaryText"
                dangerouslySetInnerHTML={{ __html: rolloverCopy }}
                data-test="rolloverCopy"
              />
              :
              <>
                {
                  shortSummaryText &&
                  <p
                    className="mn_shortSummary"
                    data-test="shortSummaryText"
                  >
                    {shortSummaryText}
                  </p>
                }
                {
                  offerTitle &&
                  <p
                    className="mn_offerTitle"
                    dangerouslySetInnerHTML={{ __html: offerTitle }}
                    data-test="offerTitle"
                  />
                }
              </>
            }
          </div>
          {hasBonus && <BonusTiers bonusTiersRef={bonusTiersRef} />}
          <div className="mn_bottomBorder" />
        </div>
      </div>
    </div>
  );
}

export default StylizedBannerItem;
